import React from 'react';

/**
 * Public privacy policy page (route: /privacy and /privacy-policy).
 *
 * Kept deliberately short and generic — it does not name internal vendors,
 * tooling, or describe how the app works. It retains only what Google requires
 * to verify the restricted gmail.readonly scope: a plain statement of what
 * Gmail data is used for, and the Limited Use disclosure.
 */

const CONTACT_EMAIL = 'broskiesgolfassociation@gmail.com';
const LAST_UPDATED = 'June 9, 2026';

const PrivacyPolicy: React.FC = () => {
  return (
    <div style={page}>
      <h1 style={{ marginBottom: 4 }}>Privacy Policy</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Broskies Golf Association &middot; Last updated: {LAST_UPDATED}
      </p>

      <p>
        The Broskies Golf Association (&ldquo;BGA&rdquo;, &ldquo;we&rdquo;) runs
        this application for our members. This policy explains, in simple terms,
        how we handle your information &mdash; including data from the optional
        Gmail connection.
      </p>

      <h2 style={h2}>Information we use</h2>
      <ul>
        <li>Account details you provide when you sign in, such as your name and email address.</li>
        <li>Golf information you enter into the app, such as rounds and scores.</li>
        <li>Reservation emails sent to your BGA email alias (see below).</li>
      </ul>

      <h2 style={h2}>Email aliases &amp; reservation tracking</h2>
      <p>
        BGA does <strong>not</strong> ask for access to your personal email
        account. Instead, we give you an <strong>email alias</strong> on our own
        domain to use when you sign up on golf reservation websites.
      </p>
      <p>
        Reservation emails sent to that alias are forwarded to our
        application&rsquo;s own inbox. Our app reads that inbox on a{' '}
        <strong>read-only</strong> basis, using Google&rsquo;s Gmail API, for a
        single purpose: to detect and track golf reservations and make them
        available to our community. We never send, change, or delete email in
        anyone&rsquo;s personal mailbox.
      </p>
      <p>
        You can set an alias to also forward a copy to your personal email for
        your own records. A copy is always routed to the BGA app so your
        reservations stay tracked and up to date.
      </p>
      <p>
        As BGA grows, we may offer additional alias domains and let members
        create their own aliases. The same principles described here will apply.
      </p>
      <p>
        We do <strong>not</strong> sell your data, use it for advertising, or use
        it to train artificial-intelligence or machine-learning models.
      </p>

      <div style={callout}>
        <p style={{ margin: 0 }}>
          Broskies Golf Association&rsquo;s use and transfer of information
          received from Google APIs to any other app will adhere to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </div>

      <h2 style={h2}>Sharing</h2>
      <p>
        We do not sell your information. We rely on a small number of trusted
        service providers to run the app, and they may only process your
        information as needed to provide their service to us.
      </p>

      <h2 style={h2}>Keeping and removing your data</h2>
      <p>
        We keep your information only as long as needed to provide the app, and
        we protect it with industry-standard security, including encrypted
        (HTTPS) connections. You can:
      </p>
      <ul>
        <li>
          Stop using your BGA alias, or ask us to deactivate or delete it, at any time.
        </li>
        <li>
          Request access to, or deletion of, your data by emailing us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </li>
      </ul>

      <h2 style={h2}>Changes</h2>
      <p>
        We may update this policy from time to time. When we do, we will revise
        the &ldquo;Last updated&rdquo; date above.
      </p>

      <h2 style={h2}>Contact</h2>
      <p>
        Questions? Email us at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </div>
  );
};

// ── Styles ──
const page: React.CSSProperties = {
  maxWidth: 820,
  margin: '0 auto',
  padding: '24px 20px 64px',
  lineHeight: 1.6,
  color: '#222',
  fontSize: 15,
};
const h2: React.CSSProperties = {
  marginTop: 28,
  marginBottom: 8,
  fontSize: 20,
  borderBottom: '1px solid #eee',
  paddingBottom: 4,
};
const callout: React.CSSProperties = {
  background: '#f3f8fc',
  border: '1px solid #cfe2f3',
  borderRadius: 8,
  padding: '14px 18px',
  margin: '18px 0',
};

export default PrivacyPolicy;
